import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { formatDate } from '@/utils/formatDateTime'

interface DateInputProps {
  /** Selected date as `yyyy-mm-dd`, or `''` for no selection — same shape a native `<input type="date">` emits. */
  value: string
  onChange: (value: string) => void
  ariaLabel: string
  /** Shown in the trigger button when `value` is empty. Defaults to `'—'`. */
  placeholder?: string
  /** Visual style for the trigger button (background/border/padding/size). Defaults to `input-field`. */
  className?: string
  /** When set, attached to the trigger for minutes validation scroll/focus. */
  dataMinutesField?: string
  /** Locks the trigger, for a form that is mid-submit. */
  disabled?: boolean
}

/** Distance between the trigger and the panel hanging off it. */
const DROPDOWN_GAP = 4

/** Smallest gap kept between the panel and any edge of the viewport. */
const VIEWPORT_MARGIN = 8

/**
 * First paint, before the panel has been measured.
 *
 * Laid out but invisible: the panel's own height decides whether it opens
 * downwards, and `useLayoutEffect` replaces this style before the browser
 * paints, so nothing is ever shown in the corner.
 */
const UNPOSITIONED: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  visibility: 'hidden',
}

/**
 * Places the calendar panel against its trigger without letting it leave the
 * viewport.
 *
 * Fixed rather than absolute because every screen that uses this puts the
 * trigger inside a scroll container, and a container with `overflow-y: auto`
 * clips the horizontal axis too — so a panel wider than its trigger came out
 * cut off rather than merely overflowing (BRAI-503).
 *
 * @param trigger Viewport rect of the button the panel hangs off.
 * @param panel Viewport rect of the panel as laid out, already width-capped.
 * @returns Inline style pinning the panel inside the viewport.
 */
function positionDropdown(trigger: DOMRect, panel: DOMRect): CSSProperties {
  const viewportWidth = document.documentElement.clientWidth
  const viewportHeight = document.documentElement.clientHeight
  // Follows the trigger's left edge until that would push the panel off the
  // right of the screen, at which point it sits against the right edge instead.
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(trigger.left, viewportWidth - panel.width - VIEWPORT_MARGIN)
  )
  const spaceBelow = viewportHeight - trigger.bottom - DROPDOWN_GAP
  const spaceAbove = trigger.top - DROPDOWN_GAP
  // Flipping only helps when above is genuinely roomier. On a short viewport
  // neither side fits, and clamping into the larger gap loses less of the
  // calendar than flipping into the smaller one would.
  const top =
    panel.height > spaceBelow && spaceAbove > spaceBelow
      ? Math.max(VIEWPORT_MARGIN, trigger.top - DROPDOWN_GAP - panel.height)
      : Math.min(
          trigger.bottom + DROPDOWN_GAP,
          Math.max(VIEWPORT_MARGIN, viewportHeight - panel.height - VIEWPORT_MARGIN)
        )
  return { position: 'fixed', top, left, zIndex: 100 }
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseDateInputValue(value: string): Date | null {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  )
}

/**
 * Builds a Sunday-start 6-week (42-day) grid for `year`/`month`, including
 * the leading/trailing days from adjacent months needed to fill it.
 */
function buildCalendarGrid(year: number, month: number): Date[] {
  const firstOfMonth = new Date(year, month, 1)
  const gridStart = new Date(year, month, 1 - firstOfMonth.getDay())
  return Array.from({ length: 42 }, (_, i) => {
    const day = new Date(gridStart)
    day.setDate(gridStart.getDate() + i)
    return day
  })
}

/**
 * Date picker with a fully custom calendar dropdown instead of a native
 * `<input type="date">`, whose displayed text/placeholder format is decided
 * by the browser/OS locale and varies across machines (Chrome/Edge respect
 * `lang`, Firefox/Safari don't). Rendering the trigger text via the shared
 * {@link formatDate} keeps the display identical everywhere (BRAI date-format
 * consistency follow-up).
 */
export default function DateInput({
  value,
  onChange,
  ariaLabel,
  placeholder = '—',
  className = 'input-field',
  dataMinutesField,
  disabled = false,
}: DateInputProps) {
  const { t, locale } = useUiStrings()
  const [open, setOpen] = useState(false)
  const selected = useMemo(() => parseDateInputValue(value), [value])
  const [viewDate, setViewDate] = useState(() => selected ?? new Date())
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>(UNPOSITIONED)

  useEffect(() => {
    if (!open) return
    const onDocumentClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Layout effect, not a plain one: the panel is positioned from its own
  // measured size, and doing that after paint would show it in the wrong place
  // for a frame. Scroll is captured so the panel follows a trigger inside any
  // scrolling ancestor, which a fixed element does not do by itself.
  useLayoutEffect(() => {
    if (!open) return

    function reposition() {
      const trigger = triggerRef.current?.getBoundingClientRect()
      const panel = panelRef.current?.getBoundingClientRect()
      if (!trigger || !panel) return
      setDropdownStyle(positionDropdown(trigger, panel))
    }

    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
      // Reopening must measure again; the trigger rarely comes back to the
      // same place, and a stale style would flash it at the old one.
      setDropdownStyle(UNPOSITIONED)
    }
  }, [open])

  const handleToggle = () => {
    if (!open) setViewDate(selected ?? new Date())
    setOpen((wasOpen) => !wasOpen)
  }

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const grid = useMemo(() => buildCalendarGrid(year, month), [year, month])
  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(viewDate),
    [locale, viewDate]
  )
  const weekdayLabels = useMemo(() => {
    // Fixed Sunday-start reference week (2023-01-01 was a Sunday) — only the
    // weekday name is read, so the actual date/year is irrelevant.
    const referenceSunday = new Date(2023, 0, 1)
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(referenceSunday)
      day.setDate(referenceSunday.getDate() + i)
      return formatter.format(day)
    })
  }, [locale])

  const today = new Date()

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-minutes-field={dataMinutesField}
        className={`flex w-full items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      >
        <span className={selected ? '' : 'text-[var(--text-tertiary)]'}>
          {selected ? formatDate(selected, locale) : placeholder}
        </span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={ariaLabel}
          style={dropdownStyle}
          className="w-72 max-w-[calc(100vw-1rem)] rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 shadow-[var(--shadow-lg)]"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              aria-label={t.previousMonth}
              className="rounded-lg p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-[var(--text-primary)]">{monthLabel}</span>
            <button
              type="button"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              aria-label={t.nextMonth}
              className="rounded-lg p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-[var(--text-tertiary)]">
            {weekdayLabels.map((label, i) => (
              <span key={i}>{label}</span>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-1">
            {grid.map((day) => {
              const inMonth = day.getMonth() === month
              const isSelected = selected ? isSameDay(day, selected) : false
              const isToday = isSameDay(day, today)
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => {
                    onChange(toDateInputValue(day))
                    setOpen(false)
                  }}
                  className={[
                    'rounded-lg py-1.5 text-sm transition-colors',
                    !inMonth
                      ? 'text-[var(--text-tertiary)] opacity-40'
                      : 'text-[var(--text-primary)]',
                    isSelected
                      ? 'bg-[var(--accent)] text-white'
                      : isToday
                        ? 'font-semibold ring-1 ring-inset ring-[var(--accent)]'
                        : 'hover:bg-[var(--bg-tertiary)]',
                  ].join(' ')}
                >
                  {day.getDate()}
                </button>
              )
            })}
          </div>

          {selected && (
            <button
              type="button"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
              className="mt-2 w-full rounded-lg py-1.5 text-center text-xs font-semibold text-[var(--accent)] hover:bg-[var(--bg-tertiary)]"
            >
              {t.clear}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
